import * as React from "react";
import { formSet, useCpFormAnyValueId } from "../../gvc/testNewForm/utils";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ContentLayout } from "../../../components/layout/contentLayout";
import { SingleNavMenuLink, toLinkButton, toLinkPage, toLinkSpace } from "../../identity/useNavMenu";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../../envVariables";
import { SingleNavMenu } from "../../../components/antd/layout/singleNavMenu";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { General } from "./general";
import { GvcSidecarEnvoy } from "../sidecarEnvoy";
import { Tracing } from "../tracing";
import { Keda } from "../keda";
import { LoadBalancer } from "../loadBalancer/loadBalancer";
import { GvcEnvVars } from "../envVars/envVars";
import { PullSecretItems } from "../pullSecretItems";
import { LocationQuery } from "../locationQuery";
import { LocationItems } from "../locationItems";
import { LocationOptions } from "../locationOptions";
import { EndpointNamingFormat } from "./endpointNamingFormat";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { CpRootError } from "../testNewForm/CpRootError";

interface Props {
  ignoreBlockerRef: React.RefObject<boolean>;
}
export const GvcCreateContent: React.FC<Props> = ({ ignoreBlockerRef }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/gvc/-create/*`);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);

  React.useEffect(() => {
    const startingPage = `${basePath}/general`;
    navigate(startingPage, { replace: true });
  }, []);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("GVC", "/general", { names: [`name`, `description`] }),
      toLinkPage("Endpoint Naming", "/endpoint-naming-format"),
      toLinkPage("Locations", "/locations"),
      toLinkPage("Location Query", "/location-query", { names: ["locationQuery"] }),
      toLinkPage("Location Options", "/location-options", { names: ["locationOptions"] }),
      toLinkPage("Pull Secrets", "/pull-secrets"),
      toLinkPage("Env Variables", "/environment-variables", { names: ["env"] }),
      toLinkPage("Load Balancer", "/load-balancer"),
      toLinkPage("Keda", "/keda"),
      toLinkPage("Tracing", "/tracing", { names: ["tracing"] }),
      toLinkPage("Sidecar | Envoy", "/sidecar-envoy", { names: ["sidecar"] }),
      toLinkPage("Tags", "/tags", { names: ["tags"] }),
      toLinkSpace(),
      toLinkButton("Cancel", {
        type: "primary",
        onClick: () => {
          ignoreBlockerRef.current = true;
          const back = pathname.split(`/-create`)[0];
          dispatch(setRedirect(back));
        },
        disabled: submitting,
        danger: true,
      }),
      toLinkButton("Preview", {
        type: "default",
        onClick: () => {
          formSet(formId, `meta.previewing`, true);
        },
        disabled: submitting,
      }),
      toLinkButton("Create", {
        type: "primary",
        onClick: async () => {
          await submit();
        },
        loading: submitting,
        disabled: submitting,
        className: "action",
      }),
    );

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
        }),
      );
    }

    return links;
  }, [submitting]);

  return (
    <BasePathContext.Provider value={basePath}>
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={false} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`general/*`} element={<General />} />
              <Route path={`endpoint-naming-format/*`} element={<EndpointNamingFormat />} />
              <Route path={`locations/*`} element={<LocationItems />} />
              <Route path={`location-options/*`} element={<LocationOptions />} />
              <Route path={`location-query/*`} element={<LocationQuery />} />
              <Route path={`pull-secrets/*`} element={<PullSecretItems />} />
              <Route path={`environment-variables/*`} element={<GvcEnvVars />} />
              <Route path={`load-balancer/*`} element={<LoadBalancer />} />
              <Route path={`keda/*`} element={<Keda />} />
              <Route path={`tracing/*`} element={<Tracing />} />
              <Route path={`sidecar-envoy/*`} element={<GvcSidecarEnvoy />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
